<script setup>
import { Head, Link, useForm } from "@inertiajs/inertia-vue3";
import Layout from "../../../Layouts/ManagementLayout.vue";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Message from "primevue/message";
import Dropdown from "primevue/dropdown";
import { computed, ref } from "vue";
import { useToast } from "primevue/usetoast";

const props = defineProps([
    'noteType',
    'companies',
    'flash',
]);

const errors = ref({});
const toast = useToast();

const form = useForm({
    company_id: props.noteType.company_id,
    name: props.noteType.name,
    color: props.noteType.color || '#000000',
});

const colors = ref([
    { name: 'Rot', code: '#EF4444' },
    { name: 'Blau', code: '#3B82F6' },
    { name: 'Grün', code: '#10B981' },
    { name: 'Gelb', code: '#F59E0B' },
    { name: 'Violett', code: '#8B5CF6' },
    { name: 'Rosa', code: '#EC4899' },
    { name: 'Indigo', code: '#6366F1' },
    { name: 'Grau', code: '#6B7280' },
    { name: 'Schwarz', code: '#000000' },
]);

const submit = () => {
    form.put(`/management/note-types/${props.noteType.uuid}`, {
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Erfolgreich',
                detail: 'Notiztyp wurde erfolgreich aktualisiert.',
                life: 5000,
            });
        },
        onError: (err) => {
            errors.value = err;
            toast.add({
                severity: 'error',
                summary: 'Fehler beim Speichern',
                detail: 'Bitte prüfen Sie die Eingaben.',
                life: 5000,
            });
        }
    });
};

const hasErrors = computed(() => Object.values(errors.value).some(v => v));

</script>

<template>
    <Layout :flash="flash">
        <Head title="Notiztyp bearbeiten" />

        <div class="text-sm">
            <h1 class="text-2xl font-bold mb-8 text-gray-800">Notiztyp bearbeiten</h1>

            <Message v-if="hasErrors" severity="error" class="mb-6">
                <template v-for="(error, key) in errors" :key="key">
                    <div v-if="error">{{ Array.isArray(error) ? error[0] : error }}</div>
                </template>
            </Message>

            <form @submit.prevent="submit" class="space-y-8">
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                        <div v-if="$page.props.auth.user.role === 'super_admin'" class="flex flex-col gap-2">
                            <label for="company_id" class="font-bold text-gray-600">Unternehmen *</label>
                             <Dropdown id="company_id" v-model="form.company_id" :options="companies" optionLabel="name" optionValue="id" :class="{'p-invalid': errors.company_id}" class="w-full" placeholder="Unternehmen wählen" @change="errors.company_id = null" />
                             <small v-if="errors.company_id" class="p-error">{{ errors.company_id }}</small>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="name" class="font-bold text-gray-600">Name des Notiztyps *</label>
                             <InputText id="name" v-model="form.name" :class="{'p-invalid': errors.name}" class="w-full" placeholder="z.B. Wichtig, Privat..." @input="errors.name = null" />
                             <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="color" class="font-bold text-gray-600">Farbe</label>
                            <Dropdown id="color" v-model="form.color" :options="colors" optionLabel="name" optionValue="code" placeholder="Farbe wählen" class="w-full">
                                <template #value="slotProps">
                                    <div v-if="slotProps.value" class="flex items-center gap-2">
                                        <div class="w-4 h-4 rounded-full" :style="{ backgroundColor: slotProps.value }"></div>
                                        <div>{{ colors.find(c => c.code === slotProps.value)?.name || slotProps.value }}</div>
                                    </div>
                                    <span v-else>
                                        {{ slotProps.placeholder }}
                                    </span>
                                </template>
                                <template #option="slotProps">
                                    <div class="flex items-center gap-2">
                                        <div class="w-4 h-4 rounded-full" :style="{ backgroundColor: slotProps.option.code }"></div>
                                        <div>{{ slotProps.option.name }}</div>
                                    </div>
                                </template>
                            </Dropdown>
                        </div>
                    </div>
                </div>

                <div class="flex justify-between items-center pt-8 border-t mt-10">
                    <Button asChild v-slot="slotProps" severity="secondary" outlined>
                        <Link href="/management/note-types" :class="slotProps.class" class="px-8 py-3 rounded-xl border-gray-300 font-bold">
                            <i class="pi pi-arrow-left mr-2" />
                            Abbrechen
                        </Link>
                    </Button>

                    <Button type="submit" 
                            class="!bg-black !text-[#FFF053] !border-none px-10 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-gray-200" 
                            :loading="form.processing" :disabled="form.processing">
                        <i class="pi pi-save" />
                        Notiztyp aktualisieren
                    </Button>
                </div>
            </form>
        </div>
    </Layout>
</template>
